import { useEffect } from 'react'
import { useCurrentOrg, getCurrentOrgId } from './orgModel'
import { updateOrganizationApiV1OrganizationsOrgIdPut } from '@/common/api/client/sdk.gen'
import type {
    OrganizationResponse,
    OrganizationUpdate,
    OrganizationWithUserRoleResponse,
} from '@/common/api/client/types.gen'
import { useQueryClient } from '@tanstack/react-query'
import { useFormMutation } from '@/common/hooks/useFormMutation'

export const useOrgForm = () => {
    const queryClient = useQueryClient()
    const { data: currentOrg, isLoading: currentOrgLoading } = useCurrentOrg()
    const currentOrgId = getCurrentOrgId()

    if (!currentOrgId) {
        throw new Error('Current org ID is not set')
    }

    const { form, handleSubmit, onSubmit, register, isLoading, isError, isSuccess, error } = useFormMutation<
        OrganizationUpdate,
        OrganizationResponse,
        {
            detail?: string
            field_errors?: Record<keyof OrganizationUpdate, string>
        }
    >({
        mutationFn: (data: OrganizationUpdate) =>
            updateOrganizationApiV1OrganizationsOrgIdPut({
                path: { org_id: currentOrgId },
                body: data,
            }).then(res => res.data!),
        toastOptions: {
            successMessage: 'Организация успешно сохранена',
        },
        onSuccess: updatedOrg => {
            queryClient.setQueryData(['current_org', updatedOrg.id], updatedOrg)
            queryClient.setQueryData(['my_orgs'], (old: OrganizationWithUserRoleResponse[] = []) =>
                old.map(org => (org.id === updatedOrg.id ? (updatedOrg as OrganizationWithUserRoleResponse) : org))
            )
            return updatedOrg
        },
    })

    useEffect(() => {
        if (currentOrg) {
            form.reset({
                name: currentOrg.name,
                description: currentOrg.description,
            })
        }
    }, [currentOrg, form])

    return {
        form,
        currentOrg,
        currentOrgLoading,
        handleSubmit,
        onSubmit,
        register,
        isPending: isLoading,
        isError,
        isSuccess,
        error,
    }
}
